import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Row,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Triangle } from "react-loader-spinner";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DestinationService from "../../../services/KangarooServices/DestinationService";
import { imageBaseApi } from "../../../utilis/imageHelper";
import { Edit3 } from "lucide-react";
import classnames from "classnames";

const DestinationDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchDetail = async () => {
      setIsLoading(true);
      try {
        const response = await DestinationService.getBySlug(id);
        console.log("🚀 ~ fetchDetail ~ response:", response);
        setData(response);
      } catch (error) {
        console.log(error);
        toast.error("Failed to fetch destination details");
        navigate("/destination-countries");
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchDetail();
    }
  }, [id, navigate]);

  if (isLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="my-5 d-flex justify-content-center align-items-center">
            <Triangle visible height="80" width="80" color="#405189" />
          </div>
        </Container>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const breadcrumbItems = [
    { title: "Back to List", link: "/destination-countries" },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Destination Detail"
          pageTitle="Destination"
          breadcrumbItems={breadcrumbItems}
        />

        {/* Banner Section */}
        <div className=" mx-n4 mt-n4">
          <div className=" bg-light">
            {data.bannerMedia ? (
              <img
                src={`${imageBaseApi}${data.bannerMedia}`}
                alt=""
                className="img-fluid w-100 object-fit-cover"
                style={{ height: "250px" }}
              />
            ) : (
              <div
                className="d-flex justify-content-center align-items-center bg-secondary text-white"
                style={{ height: "280px" }}
              >
                <span>No Banner Provided</span>
              </div>
            )}
          </div>
        </div>

        <div className="pt-4 mb-4">
          {/* Header Section */}

          <Row>
            <Col lg={4}>
              <Card className=" shadow-sm mb-4">
                <CardBody>
                  <Row className="align-items-center">
                    {/* Flag + Title */}
                    <Col lg={8}>
                      <div className="d-flex align-items-center gap-3">
                        <div>
                          <h2 className="fw-bold mb-1">{data.name}</h2>
                          <Badge
                            pill
                            color={data.isActive ? "success" : "secondary"}
                            className="px-3 py-2"
                          >
                            {data.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                      </div>
                    </Col>

                    {/* Action Button */}
                    <Col lg={4} className="text-lg-end mt-3 mt-lg-0">
                      <Button
                        color="primary"
                        size="sm"
                        className="d-flex align-items-center gap-2"
                        onClick={() =>
                          navigate(`/destination-countries/update/${data.id}`)
                        }
                      >
                        <Edit3 size={14} />
                        Update
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
              <Card>
                <CardBody>
                  <div
                    className="position-relative"
                    style={{ aspectRatio: "16/9", overflow: "hidden" }}
                  >
                    {data.flagMedia && (
                      <img
                        src={`${imageBaseApi}${data.flagMedia.url}`}
                        alt={data.name}
                        className="rounded border"
                      />
                    )}
                  </div>
                </CardBody>
              </Card>
              {/* Metadata */}
              <Card className=" shadow-sm bg-light mt-4">
                <CardBody>
                  <Row className="text-muted small">
                    <Col md={12}>
                      <i className="ri-calendar-line me-1"></i>
                      <strong>Created:</strong>{" "}
                      {new Date(data.createdAt).toLocaleString()}
                    </Col>
                    <Col md={12}>
                      <i className="ri-time-line me-1"></i>
                      <strong>Last Updated:</strong>{" "}
                      {data.updatedAt
                        ? new Date(data.updatedAt).toLocaleString()
                        : "—"}
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
            <Col>
              {/* Description */}
              {data.description && (
                <Card className=" shadow-sm mb-2">
                  <CardHeader className="bg-transparent border-bottom">
                    <h5 className="fw-semibold mb-0">
                      <i className="ri-file-text-line me-2 text-primary" />
                      Description
                    </h5>
                  </CardHeader>
                  <CardBody>
                    <div
                      className="text-muted"
                      dangerouslySetInnerHTML={{
                        __html: data?.description,
                      }}
                    />
                  </CardBody>
                </Card>
              )}

              {/* Info Boxes */}
              {data?.subDetails?.map((idx) => (
                <Card key={idx} className=" shadow-sm mb-2">
                  <CardHeader className="bg-transparent border-bottom d-flex align-items-center">
                    {/* <i className={`${item.icon} fs-4 me-2`}></i> */}
                    <h5 className="fw-semibold mb-0">{idx.title}</h5>
                  </CardHeader>

                  <CardBody>
                    {/* <p className="text-muted">{item.value}</p> */}
                    <div
                      className="text-muted"
                      dangerouslySetInnerHTML={{
                        __html: idx?.description,
                      }}
                    />
                    <img
                      src={`${imageBaseApi}${idx?.media?.url}`}
                      alt={idx.name}
                      className="rounded border"
                    />
                  </CardBody>
                </Card>
              ))}

              {/* Post Study Work */}
              {data.postStudyWork && (
                <Card className=" shadow-sm mb-2">
                  <CardHeader className="bg-transparent border-bottom">
                    <h5 className="fw-semibold mb-0">
                      <i className="ri-user-star-line me-2 text-warning" />
                      Post-Study Work Opportunities
                    </h5>
                  </CardHeader>
                  <CardBody>
                    {/* <p className="text-muted">{data.postStudyWork}</p> */}
                    <div
                      className="text-muted"
                      dangerouslySetInnerHTML={{
                        __html: data?.postStudyWork,
                      }}
                    />
                  </CardBody>
                </Card>
              )}

              {/* Sub Details Section */}
              {data.subDetails?.length > 0 && (
                <Card className=" shadow-sm mb-2">
                  <CardHeader className="bg-transparent border-bottom">
                    <h5 className="fw-semibold mb-0">
                      <i className="ri-information-line me-2 text-primary" />
                      Additional Information
                    </h5>
                  </CardHeader>
                  <CardBody>
                    <Row className="">
                      {data.subDetails.map((item) => (
                        <Col md={6} lg={4} key={item.id}>
                          <Card className="h-100 border hover-shadow">
                            <img
                              src={`${imageBaseApi}${item.media?.url}`}
                              className="img-fluid rounded-top"
                              style={{ height: "160px", objectFit: "contain" }}
                              alt={item.title}
                            />
                            <CardBody>
                              <h6 className="fw-semibold mb-2">{item.title}</h6>
                              {/* <p className="text-muted small">
                                {item.description}
                              </p> */}
                              <div
                                className="text-muted"
                                dangerouslySetInnerHTML={{
                                  __html: item?.description,
                                }}
                              />
                              {item.link && (
                                <a
                                  href={item.link}
                                  className="btn btn-sm btn-outline-primary mt-2"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Learn More →
                                </a>
                              )}
                            </CardBody>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </CardBody>
                </Card>
              )}
            </Col>
          </Row>
        </div>
      </Container>
    </div>
  );
};

export default DestinationDetail;
